/* iter55z-audit round 9 (extras 2) — Email Workers CLI panel.
 *
 * Two tabs (Inbound / Outbound). Each tab shows:
 *   - Editable form (name, compat date, backend URL / allowed senders,
 *     workers_dev toggle)
 *   - Live-generated wrangler.jsonc preview with copy + download
 *   - Numbered CLI runbook the operator copy-pastes into their
 *     terminal to authenticate wrangler and deploy
 *
 * Backend: /api/admin/email-workers/{config|<name>/wrangler.jsonc|<name>/cli}
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Save, Download, Copy, Plus, Trash2, Terminal, RefreshCw, FileCode,
} from 'lucide-react';

const API  = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

const copyToClipboard = async (text) => {
  try { await navigator.clipboard.writeText(text); toast.success('Copied.'); }
  catch { toast.error('Copy failed.'); }
};

function downloadFile(name, body) {
  const blob = new Blob([body], { type: 'application/jsonc' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

/* ─── Preview + CLI runbook ───────────────────────────────────────── */
function WranglerPreview({ which, tick }) {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await http.get(`/admin/email-workers/${which}/wrangler.jsonc`);
      setBody(r.data.body);
    } catch (e) {
      toast.error(`Preview failed: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  }, [which]);
  useEffect(() => { load(); }, [load, tick]);
  return (
    <div data-testid={`email-workers-preview-${which}`}
         className="rounded-md border border-border bg-card p-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
          <FileCode className="h-3.5 w-3.5" /> wrangler.jsonc preview
        </div>
        <div className="flex items-center gap-1">
          <button onClick={load} disabled={busy}
                  data-testid={`email-workers-refresh-${which}`}
                  className="rounded-sm bg-secondary p-1 hover:bg-secondary/80">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          </button>
          <button onClick={() => copyToClipboard(body)}
                  data-testid={`email-workers-copy-${which}`}
                  className="rounded-sm bg-secondary p-1 hover:bg-secondary/80">
            <Copy className="h-3 w-3" />
          </button>
          <button onClick={() => downloadFile('wrangler.jsonc', body)}
                  data-testid={`email-workers-download-${which}`}
                  className="rounded-sm bg-secondary p-1 hover:bg-secondary/80">
            <Download className="h-3 w-3" />
          </button>
        </div>
      </div>
      <pre className="max-h-64 cursor-text select-text overflow-auto rounded-sm bg-neutral-950 p-2 font-mono text-[10.5px] leading-relaxed text-emerald-300"
           data-testid={`email-workers-body-${which}`}>{body}</pre>
    </div>
  );
}

function CliRunbook({ which, tick }) {
  const [steps, setSteps] = useState([]);
  const [meta,  setMeta]  = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await http.get(`/admin/email-workers/${which}/cli`);
      setSteps(r.data.steps);
      setMeta({ name: r.data.name, worker_dir: r.data.worker_dir });
    } catch (e) {
      toast.error(`Runbook failed: ${e.response?.data?.detail || e.message}`);
    }
  }, [which]);
  useEffect(() => { load(); }, [load, tick]);
  // iter55z-audit round 9 (extras 3) — Download the entire runbook as
  // a POSIX shell script so operators can pipe it into `bash` after
  // reviewing. Every step becomes a commented-out header + the raw
  // command; the operator uncomments the ones they want to actually
  // run (or just executes selectively).
  const downloadScript = () => {
    const body =
      `#!/usr/bin/env bash\n` +
      `# Auto-generated by /api/admin/email-workers/${which}/cli\n` +
      `# Worker: ${meta?.name || which} · dir: ${meta?.worker_dir || ''}\n` +
      `# Review before piping into bash. Some steps expect stdin prompts\n` +
      `# (secret values), which is why we do NOT chain with && or set -e.\n\n` +
      steps.map((s, i) =>
        `# ${i + 1}. ${s.label}\n` +
        (s.hint ? `# hint: ${s.hint}\n` : '') +
        `${s.cmd}\n`,
      ).join('\n');
    const blob = new Blob([body], { type: 'text/x-shellscript' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `${which}-worker-runbook.sh`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 500);
  };
  return (
    <div data-testid={`email-workers-cli-${which}`}
         className="rounded-md border border-border bg-card p-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
          <Terminal className="h-3.5 w-3.5" /> CLI runbook
        </div>
        <div className="flex items-center gap-1">
          {meta && <span className="rounded-full bg-secondary px-2 py-0.5 font-mono text-[10.5px]"
                          data-testid={`email-workers-cli-name-${which}`}>{meta.name}</span>}
          <button onClick={downloadScript}
                  disabled={!steps.length}
                  data-testid={`email-workers-cli-download-${which}`}
                  title="Download entire runbook as .sh"
                  className="rounded-sm bg-secondary p-1 hover:bg-secondary/80 disabled:opacity-50">
            <Download className="h-3 w-3" />
          </button>
        </div>
      </div>
      <ol className="space-y-2">
        {steps.map((s, i) => (
          <li key={i} data-testid={`email-workers-cli-step-${which}-${i}`}
              className="rounded-sm border border-border/60 bg-neutral-950 p-2">
            <div className="mb-1 text-[10.5px] uppercase tracking-[0.14em] text-neutral-400">
              {s.label}
            </div>
            <div className="flex items-start gap-2">
              {/* iter55z-audit round 9 (extras 3) — Explicit `user-select-text`
                  + a fully selectable input variant so the whole command
                  can be double-clicked / drag-selected inside the black
                  panel. Prior <code> was already user-selectable in most
                  browsers but iOS Safari occasionally intercepts long-press;
                  the invisible ::before spacer forces a clean drag origin. */}
              <code
                className="flex-1 cursor-text select-text whitespace-pre-wrap break-all font-mono text-[11.5px] text-emerald-300"
                data-testid={`email-workers-cli-cmd-${which}-${i}`}
                onClick={(e) => {
                  // Triple-click / double-click on empty area shouldn't
                  // grab the whole card — only the command line.
                  const sel = window.getSelection?.();
                  if (sel && sel.rangeCount === 0) {
                    const range = document.createRange();
                    range.selectNodeContents(e.currentTarget);
                    sel.addRange(range);
                  }
                }}
              >
                $ {s.cmd}
              </code>
              <button onClick={() => copyToClipboard(s.cmd)}
                      data-testid={`email-workers-cli-copy-${which}-${i}`}
                      className="rounded-sm bg-neutral-800 p-1 text-neutral-300 hover:bg-neutral-700">
                <Copy className="h-3 w-3" />
              </button>
            </div>
            {s.hint && (
              <div className="mt-1 text-[10.5px] text-neutral-400">{s.hint}</div>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

/* ─── Inbound editor ──────────────────────────────────────────────── */
function InboundEditor({ cfg, onChange, onSave, saving, tick }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
          Worker name
          <input value={cfg.name} onChange={(e) => onChange({ ...cfg, name: e.target.value })}
                 data-testid="email-workers-inbound-name"
                 className="mt-1 h-7 w-full rounded-sm border border-border bg-background px-2 font-mono text-[11.5px]" />
        </label>
        <label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
          Compatibility date
          <input type="date" value={cfg.compatibility_date}
                 onChange={(e) => onChange({ ...cfg, compatibility_date: e.target.value })}
                 data-testid="email-workers-inbound-compat"
                 className="mt-1 h-7 w-full rounded-sm border border-border bg-background px-2 font-mono text-[11.5px]" />
        </label>
        <label className="col-span-full text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
          Backend URL (BACKEND_URL secret)
          <input value={cfg.backend_url} onChange={(e) => onChange({ ...cfg, backend_url: e.target.value })}
                 data-testid="email-workers-inbound-backend-url"
                 className="mt-1 h-7 w-full rounded-sm border border-border bg-background px-2 font-mono text-[11.5px]" />
        </label>
        <label className="text-[11px]">
          <input type="checkbox" checked={cfg.workers_dev}
                 onChange={(e) => onChange({ ...cfg, workers_dev: e.target.checked })}
                 data-testid="email-workers-inbound-workers-dev"
                 className="mr-2 accent-primary" />
          Enable workers.dev preview subdomain
        </label>
      </div>
      <button onClick={onSave} disabled={saving}
              data-testid="email-workers-inbound-save"
              className="inline-flex items-center gap-1 rounded-sm bg-primary px-2 py-1 text-[11.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
        Save config
      </button>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <WranglerPreview which="inbound" tick={tick} />
        <CliRunbook which="inbound" tick={tick} />
      </div>
    </div>
  );
}

/* ─── Outbound editor ─────────────────────────────────────────────── */
function OutboundEditor({ cfg, onChange, onSave, saving, tick }) {
  const setSenders = (list) => onChange({ ...cfg, allowed_senders: list });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
          Worker name
          <input value={cfg.name} onChange={(e) => onChange({ ...cfg, name: e.target.value })}
                 data-testid="email-workers-outbound-name"
                 className="mt-1 h-7 w-full rounded-sm border border-border bg-background px-2 font-mono text-[11.5px]" />
        </label>
        <label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
          Compatibility date
          <input type="date" value={cfg.compatibility_date}
                 onChange={(e) => onChange({ ...cfg, compatibility_date: e.target.value })}
                 data-testid="email-workers-outbound-compat"
                 className="mt-1 h-7 w-full rounded-sm border border-border bg-background px-2 font-mono text-[11.5px]" />
        </label>
      </div>
      <div>
        <div className="mb-1 text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
          Allowed sender addresses
        </div>
        {cfg.allowed_senders.map((addr, i) => (
          <div key={i} className="mb-1 flex gap-1"
               data-testid={`email-workers-outbound-sender-${i}`}>
            <input value={addr} onChange={(e) => {
              const next = [...cfg.allowed_senders]; next[i] = e.target.value; setSenders(next);
            }}
                   className="h-7 flex-1 rounded-sm border border-border bg-background px-2 font-mono text-[11.5px]" />
            <button onClick={() => setSenders(cfg.allowed_senders.filter((_, j) => j !== i))}
                    data-testid={`email-workers-outbound-sender-del-${i}`}
                    className="rounded-sm p-1 text-destructive hover:bg-destructive/10">
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        ))}
        <button onClick={() => setSenders([...cfg.allowed_senders, 'info@example.com'])}
                data-testid="email-workers-outbound-sender-add"
                className="mt-1 inline-flex items-center gap-1 rounded-sm bg-secondary px-2 py-0.5 text-[11px] hover:bg-secondary/80">
          <Plus className="h-3 w-3" /> Add sender
        </button>
      </div>
      <label className="text-[11px]">
        <input type="checkbox" checked={cfg.workers_dev}
               onChange={(e) => onChange({ ...cfg, workers_dev: e.target.checked })}
               data-testid="email-workers-outbound-workers-dev"
               className="mr-2 accent-primary" />
        Enable workers.dev preview subdomain
      </label>
      <button onClick={onSave} disabled={saving}
              data-testid="email-workers-outbound-save"
              className="inline-flex items-center gap-1 rounded-sm bg-primary px-2 py-1 text-[11.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
        Save config
      </button>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <WranglerPreview which="outbound" tick={tick} />
        <CliRunbook which="outbound" tick={tick} />
      </div>
    </div>
  );
}

/* ─── Panel ───────────────────────────────────────────────────────── */
export default function EmailWorkersCliPanel() {
  const [cfg,   setCfg]   = useState(null);
  const [tab,   setTab]   = useState('inbound');
  const [saving, setSaving] = useState(false);
  // Bumped whenever save succeeds so previews refetch.
  const [tick, setTick]   = useState(0);
  useEffect(() => {
    http.get('/admin/email-workers/config')
      .then((r) => setCfg(r.data))
      .catch((e) => toast.error(`Load failed: ${e.response?.data?.detail || e.message}`));
  }, []);
  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      await http.put('/admin/email-workers/config', cfg);
      toast.success('Saved.');
      setTick((n) => n + 1);
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally { setSaving(false); }
  };

  if (!cfg) {
    return (
      <div className="flex h-64 items-center justify-center"
           data-testid="email-workers-panel-loading">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }
  return (
    <div data-testid="email-workers-panel" className="space-y-4">
      <div className="rounded-md border border-border bg-card p-3">
        <div className="mb-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
          Cloudflare Email Workers — CLI generator
        </div>
        <div className="text-[12px] text-muted-foreground">
          Wrangler runs on <b>your</b> machine — the pod can't reach Cloudflare's
          API on your behalf. Save your config below, copy the generated
          <code className="mx-1 rounded-sm bg-secondary px-1 font-mono">wrangler.jsonc</code>
          into the worker folder, then run the numbered CLI steps.
        </div>
      </div>
      <div className="flex gap-1 border-b border-border">
        {['inbound', 'outbound'].map((t) => (
          <button key={t} onClick={() => setTab(t)}
                  data-testid={`email-workers-tab-${t}`}
                  className={`px-3 py-1.5 text-[12px] font-semibold uppercase tracking-wider
                    ${tab === t ? 'border-b-2 border-primary text-primary'
                                : 'text-muted-foreground hover:text-foreground'}`}>
            {t} worker
          </button>
        ))}
      </div>
      {tab === 'inbound' ? (
        <InboundEditor cfg={cfg.inbound}
                       onChange={(next) => setCfg({ ...cfg, inbound: next })}
                       onSave={save} saving={saving} tick={tick} />
      ) : (
        <OutboundEditor cfg={cfg.outbound}
                        onChange={(next) => setCfg({ ...cfg, outbound: next })}
                        onSave={save} saving={saving} tick={tick} />
      )}
    </div>
  );
}
